import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import LoadingPage from "../../components/LoadingPage";
import { NotificationStack } from "../../components/ui";
import { PlanningProvider, usePlanningContext } from "../../context/domain/planning";
import ProjectFormModal from "../../features/projects/shared/ProjectFormModal";
import { updateProject } from "../../services/api/projects";
import ProjectAssistant from "../../features/projects/projectPage/ProjectAssistant";
import ProjectDispatch from "../../features/projects/projectPage/ProjectDispatch";
import ProjectDocuments from "../../features/projects/projectPage/ProjectDocuments";
import ProjectHeader from "../../features/projects/projectPage/ProjectHeader";
import ProjectOverview from "../../features/projects/projectPage/ProjectOverview";
import ProjectSchedule from "../../features/projects/projectPage/ProjectSchedule";
import ProjectTabs from "../../features/projects/projectPage/ProjectTabs";
import { PROJECT_TABS } from "../../features/projects/constants";
import {
  ProjectPageProvider,
  useProjectPageContext,
} from "../../features/projects/projectPage/ProjectPageContext";

/**
 * Host planning operation toasts for Schedule/Dispatch embeds on ProjectPage.
 *
 * @returns {JSX.Element}
 */
const ProjectPlanningNotificationHost = () => {
  const { notificationItems, dismissNotification } = usePlanningContext();
  return (
    <NotificationStack
      items={notificationItems ?? []}
      onDismiss={dismissNotification}
    />
  );
};

/**
 * Keep a visited project tab mounted and toggle visibility so switches do not
 * reload data — but only mount a tab the first time it is selected (Overview
 * mounts immediately; Schedule/Dispatch stay cold until opened).
 *
 * @param {Object} props - Component props.
 * @param {string} props.tabId - Tab id this panel belongs to.
 * @param {string} props.activeTab - Currently selected tab id.
 * @param {boolean} props.hasMounted - Whether this tab has been visited.
 * @param {React.ReactNode} props.children - Tab content.
 * @returns {JSX.Element|null}
 */
function ProjectTabPanel({ tabId, activeTab, hasMounted, children }) {
  if (!hasMounted) {
    return null;
  }
  const isActive = activeTab === tabId;
  return (
    <div className={isActive ? undefined : "hidden"} aria-hidden={!isActive}>
      {children}
    </div>
  );
}

/**
 * Render project page content inside ProjectPageProvider.
 *
 * @returns {JSX.Element}
 */
const ProjectPageContent = () => {
  const {
    project,
    activeTab,
    editingProject,
    closeEditProject,
    handleProjectSaved,
    isLoading,
    error,
    refreshProject,
  } = useProjectPageContext();

  // Lazy-mount heavy tabs (Schedule/Dispatch/Documents) on first visit only.
  const [mountedTabs, setMountedTabs] = useState(() => new Set(["overview"]));

  useEffect(() => {
    setMountedTabs((prev) => {
      if (prev.has(activeTab)) {
        return prev;
      }
      const next = new Set(prev);
      next.add(activeTab);
      return next;
    });
  }, [activeTab]);

  useEffect(() => {
    const tabLabel =
      PROJECT_TABS.find((tab) => tab.id === activeTab)?.label ?? "";
    if (!project?.number) {
      document.title = tabLabel ? `R&S Project - ${tabLabel}` : "R&S Project";
      return;
    }
    document.title = tabLabel
      ? `R&S Project - ${project.number}: ${tabLabel}`
      : `R&S Project - ${project.number}`;
  }, [project?.number, activeTab]);

  // Scroll again after content replaces LoadingPage (layout can restore scrollY).
  useEffect(() => {
    if (isLoading || !project?.id) {
      return;
    }
    window.scrollTo(0, 0);
  }, [isLoading, project?.id]);

  if (isLoading) {
    return <LoadingPage text="R&S Project" />;
  }

  if (error || !project) {
    return (
      <div className="min-h-screen bg-custom-rsbg px-4 py-6 font-inter">
        <main className="mx-auto max-w-3xl rounded-lg border border-red-200 bg-white p-6 text-red-700">
          {error || "Project could not be loaded."}
          <div className="mt-4 flex flex-wrap gap-3">
            {typeof refreshProject === "function" ? (
              <button
                type="button"
                onClick={() => refreshProject()}
                className="rounded-md bg-primary px-3 py-1.5 text-sm font-semibold text-white hover:bg-primary-300"
              >
                Retry
              </button>
            ) : null}
            <Link
              to="/projects"
              className="font-medium text-primary-green hover:underline"
            >
              Back to Projects
            </Link>
          </div>
        </main>
      </div>
    );
  }

  return (
    <main className="min-h-screen bg-custom-rsbg font-inter text-gray-900">
      <div className="relative isolate">
        <div className="absolute inset-0 -z-10 bg-rsbg" />

        <ProjectHeader />

        <div className="mx-auto flex max-w-7xl flex-col gap-2 px-1 py-2 sm:px-2 lg:px-3">
          <ProjectTabs />

          <ProjectTabPanel
            tabId="overview"
            activeTab={activeTab}
            hasMounted={mountedTabs.has("overview")}
          >
            <ProjectOverview />
          </ProjectTabPanel>
          <ProjectTabPanel
            tabId="schedule"
            activeTab={activeTab}
            hasMounted={mountedTabs.has("schedule")}
          >
            <ProjectSchedule />
          </ProjectTabPanel>
          <ProjectTabPanel
            tabId="dispatch"
            activeTab={activeTab}
            hasMounted={mountedTabs.has("dispatch")}
          >
            <ProjectDispatch />
          </ProjectTabPanel>
          <ProjectTabPanel
            tabId="documents"
            activeTab={activeTab}
            hasMounted={mountedTabs.has("documents")}
          >
            <ProjectDocuments />
          </ProjectTabPanel>
          <ProjectTabPanel
            tabId="assistant"
            activeTab={activeTab}
            hasMounted={mountedTabs.has("assistant")}
          >
            <ProjectAssistant />
          </ProjectTabPanel>
        </div>
      </div>

      <ProjectFormModal
        open={Boolean(editingProject)}
        mode="edit"
        project={editingProject}
        onSubmit={(payload) => updateProject(editingProject.id, payload)}
        onClose={closeEditProject}
        onSaved={handleProjectSaved}
      />
    </main>
  );
};

/**
 * Render the project detail page for a single project.
 *
 * Performance: Schedule/Dispatch providers are not mounted until those tabs
 * are first opened. Overview (metrics) loads immediately.
 *
 * @returns {JSX.Element}
 */
const ProjectPage = () => {
  const { projectId } = useParams();

  // Reset window scroll on entry so navigation from a scrolled Projects Home
  // does not land mid-page with the sticky header already collapsed.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [projectId]);

  return (
    <ProjectPageProvider projectId={projectId}>
      <PlanningProvider>
        <ProjectPlanningNotificationHost />
        <ProjectPageContent />
      </PlanningProvider>
    </ProjectPageProvider>
  );
};

export default ProjectPage;
